import React, { lazy, Suspense, useEffect } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import "./App.css";
import Home from "./components/Home/Home";
import ProtectedRoutes from './components/ProtectedRoutes';
import PublicRoutes from './components/PublicRoutes';
import { ensureMetaPixelBootstrap } from "./utils/helpers/metaPixel";

const CookLogin = lazy(() => import("./components/auth_screens/CookLogin"));
const EmployerLogin = lazy(() => import("./components/auth_screens/EmployerLogin"));
const EmployerSignup = lazy(() => import("./components/auth_screens/EmployerSignup"));
const EmployerSignupLinkRedirect = lazy(
  () => import("./components/auth_screens/EmployerSignupLinkRedirect")
);
const CatererSignup = lazy(() => import("./components/auth_screens/CatererSignup"));
const CookSignup = lazy(() => import("./components/auth_screens/CookSignup"));
const Dashboard = lazy(() => import("./components/employer/Dashboard"));
const ClientDashBoard = lazy(() => import("./components/client_dashboard/ClientDashBoard"));
const CookDashboard = lazy(() => import("./components/cook_dashboard/CookDashboard"));
const CookVerify = lazy(() => import("./components/auth_screens/CookVerify"));
const EmployerVerify = lazy(() => import("./components/auth_screens/employerVerify"));
const ForgotPassword = lazy(() => import("./components/auth_screens/ForgotPassword"));
const UpdatePassword = lazy(() => import("./components/auth_screens/updatepassword"));
const FAQs = lazy(() => import("./components/auth_screens/faq"));
const Termsandconditions = lazy(() => import("./components/auth_screens/Termsandconditions"));
const PrivacyPolicy = lazy(() => import("./components/auth_screens/privacy-policy"));
const RefundPolicy = lazy(() => import("./components/auth_screens/refundpolicy"));
const ContactUs = lazy(() => import("./components/auth_screens/ContactUs"));
const About = lazy(() => import("./components/auth_screens/about"));
const SuccessEmail = lazy(() => import("./components/auth_screens/successemail.jsx"));
const FailEmail = lazy(() => import("./components/auth_screens/failemail.jsx"));
const NotFoundPage = lazy(() => import("./utils/helpers/Nofound/NotfoundPage.jsx"));
const WebsiteCooksList = lazy(() => import("./components/auth_screens/WebsiteCooks.jsx"));
const ChefMarketplace = lazy(() => import("./components/auth_screens/ChefMarketplace.jsx"));
const HireChef = lazy(() => import("./components/auth_screens/HireChef.jsx"));
const HireTalentRedirect = lazy(() => import("./components/Home/HireTalentRedirect.jsx"));
const ErrorPage = lazy(() => import("./components/auth_screens/404.jsx"));
const FindJobPage = lazy(() => import("./components/auth_screens/FindJobPage/FindJobPage.jsx"));
const HouseCookJobsPage = lazy(() => import("./components/auth_screens/FindJobPage/HouseCookJobsPage.jsx"));
const PublicJobDetailPage = lazy(() => import("./components/auth_screens/FindJobPage/PublicJobDetailPage.jsx"));
const HorecasPage = lazy(() => import("./app/horecas/page.jsx"));
const HouseholdsPage = lazy(() => import("./app/households/page.jsx"));
const CookChefPage = lazy(() => import("./app/cook/chef/page.jsx"));
const CookHouseCookPage = lazy(() => import("./app/cook/housecook/page.jsx"));
const CookPartyCookPage = lazy(() => import("./app/cook/partycook/page.jsx"));
const CookCateringPage = lazy(() => import("./app/cook/catering/page.jsx"));
const HorecaRestaurantsPage = lazy(() => import("./app/horeca/restaurants/page.jsx"));
const AppLinkLanding = lazy(() => import("./app/AppLinkLanding.jsx"));

// Hindi routes remain available by direct URL, but their code is not downloaded
// during the default English experience.
const Dashboardh = lazy(() => import("./components/Hindi/employer/Dashboard"));
const ClientDashBoardh = lazy(() => import("./components/Hindi/client_dashboard/ClientDashBoard"));
const CookDashboardh = lazy(() => import("./components/Hindi/cook_dashboard/CookDashboard"));
const CateringSignuph = lazy(() => import("./components/Hindi/auth_screens/CateringSignup"));
const EmployerLoginh = lazy(() => import("./components/Hindi/auth_screens/EmployerLogin"));
const CookLoginh = lazy(() => import("./components/Hindi/auth_screens/CookLogin"));
const ForgotPasswordh = lazy(() => import("./components/Hindi/auth_screens/ForgotPassword"));
const UpdatePasswordh = lazy(() => import("./components/Hindi/auth_screens/updatepassword"));
const CookSignuph = lazy(() => import("./components/Hindi/auth_screens/CookSignup"));
const EmployerSignuph = lazy(() => import("./components/Hindi/auth_screens/EmployerSignup"));
const CookVerifyh = lazy(() => import("./components/Hindi/auth_screens/CookVerify"));
const EmployerVerifyh = lazy(() => import("./components/Hindi/auth_screens/employerVerify"));
const FAQsh = lazy(() => import("./components/Hindi/client_dashboard/Pages/faq"));
const Termsandconditionsh = lazy(() => import("./components/Hindi/auth_screens/Termsandconditions"));
const PrivacyPolicyh = lazy(() => import("./components/Hindi/auth_screens/privacy-policy"));
const RefundPolicyh = lazy(() => import("./components/Hindi/auth_screens/refundpolicy"));
const ContactUsh = lazy(() => import("./components/Hindi/auth_screens/ContactUs"));
const Abouth = lazy(() => import("./components/Hindi/auth_screens/about"));
const ExternalRedirect = () => {
  window.location.href = "https://play.google.com/store/apps/details?id=in.cookandchef";
  return null;
};

function App() {
  useEffect(() => {
    ensureMetaPixelBootstrap();
  }, []);

  return (
    <Suspense fallback={<div role="status" className="route-loading">Loading…</div>}>
      <Routes>
      <Route path="/faq" element={<FAQs />} />
      <Route path="/terms-and-conditions" element={<Termsandconditions />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/about" element={<About />} />
      {/* SEO content hubs — must stay above protected /cook/* */}
      <Route path="/horecas" element={<HorecasPage />} />
      <Route path="/horeca/restaurants" element={<HorecaRestaurantsPage />} />
      <Route path="/hire-chef" element={<HireChef />} />
      <Route path="/households" element={<HouseholdsPage />} />
      <Route path="/cook/chef" element={<CookChefPage />} />
      <Route path="/cook/housecook" element={<CookHouseCookPage />} />
      <Route path="/cook/partycook" element={<CookPartyCookPage />} />
      <Route path="/cook/catering" element={<CookCateringPage />} />
      <Route path="/hin/faq" element={<FAQsh />} />
      <Route path="/hin/terms-and-conditions" element={<Termsandconditionsh />} />
      <Route path="/hin/privacy-policy" element={<PrivacyPolicyh />} />
      <Route path="/hin/refund-policy" element={<RefundPolicyh />} />
      <Route path="/hin/contact" element={<ContactUsh />} />
      <Route path="/hin/about" element={<Abouth />} />
      <Route path="/app/find-chefs" element={<AppLinkLanding />} />
      <Route path="/app/find-chef" element={<AppLinkLanding />} />
      <Route path="/app/find-jobs" element={<AppLinkLanding />} />
      <Route path="/app/find-job" element={<AppLinkLanding />} />
      <Route path="/app/employer/*" element={<AppLinkLanding />} />
      <Route path="/app/cook/*" element={<AppLinkLanding />} />
      <Route path="/app/employer" element={<AppLinkLanding />} />
      <Route path="/app/cook" element={<AppLinkLanding />} />
      <Route path="/app" element={<AppLinkLanding />} />
      <Route path="/" element={<PublicRoutes />}>
        <Route index element={<Home />} />
        <Route path="/Caterer-signup" element={<CatererSignup />} />
        <Route path="/catering_signup" element={<CatererSignup />} />
        <Route
          path="/client_signup"
          element={<Navigate to="/employer-registration" replace />}
        />
        <Route path="/employer-login" element={<EmployerLogin />} />
        <Route path="/login" element={<CookLogin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/cook-signup" element={<CookSignup />} />
        <Route path="/employer-registration" element={<EmployerSignup />} />
        <Route
          path="/employer/signup"
          element={<EmployerSignupLinkRedirect targetPath="/employer-registration" />}
        />
        <Route path="/cook-verify-otp" element={<CookVerify />} />
        <Route path="/employer-verify-otp" element={<EmployerVerify />} />
        <Route path="/cooks/chef/*" element={<ChefMarketplace />} />
        <Route path="/cooks/:name" element={<WebsiteCooksList />} />
        <Route path="/hire/:slug" element={<HireTalentRedirect />} />
        <Route path="/success-email" element={<SuccessEmail />} />
        <Route path="/fail-email" element={<FailEmail />} />
        <Route path="/hin/catering_signup" element={<CateringSignuph />} />
        <Route
          path="/hin/client_signup"
          element={<Navigate to="/hin/employer-registration" replace />}
        />
        <Route path="/hin/employer-login" element={<EmployerLoginh />} />
        <Route path="/hin/login" element={<CookLoginh />} />
        <Route path="/hin/forgot-password" element={<ForgotPasswordh />} />
        <Route path="/hin/update-password" element={<UpdatePasswordh />} />
        <Route path="/hin/cook-signup" element={<CookSignuph />} />
        <Route path="/hin/employer-registration" element={<EmployerSignuph />} />
        <Route
          path="/hin/employer/signup"
          element={
            <EmployerSignupLinkRedirect targetPath="/hin/employer-registration" />
          }
        />
        <Route path="/hin/cook-verify-otp" element={<CookVerifyh />} />
        <Route path="/hin/employer-verify-otp" element={<EmployerVerifyh />} />
        <Route path="/chef-jobs" element={<FindJobPage />} />
        <Route path="/chef-jobs/:slug" element={<PublicJobDetailPage />} />
        <Route path="/housecook-jobs" element={<HouseCookJobsPage />} />
      </Route>
      <Route path="/" element={<ProtectedRoutes />}>
        <Route path="/employer/*" element={<Dashboard />} />
        <Route path="/client/*" element={<ClientDashBoard />} />
        <Route path="/cook/*" element={<CookDashboard />} />
        <Route path="/employer/not-found" element={<NotFoundPage />} />
        <Route path="/hin/employer/*" element={<Dashboardh />} />
        <Route path="/hin/client/*" element={<ClientDashBoardh />} />
        <Route path="/hin/cook/*" element={<CookDashboardh />} />
      </Route>
      <Route path="*" element={<ErrorPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;